/**
 * Performance strip — top latency contributors and tool call summary.
 *
 * CSS: styles/components/perf-strip.css
 */

import { esc } from '../../shared/rendering.js'
import type { PerfStripProps } from '../../types/components.js'

const FILL_COLORS = ['pf1', 'pf2', 'pf3', 'pf4'] as const
const TH = 'style="padding:3px 8px;text-align:right;font-family:var(--mono);font-size:10px"'
const TD_L = 'style="padding:3px 8px;text-align:left;font-family:var(--mono);font-size:10px"'
const TH_L = 'style="padding:3px 8px;text-align:left;font-size:9px;color:var(--t4);font-weight:500"'

function renderContributorRow(
  label: string,
  percent: number,
  avgDuration: string,
  totalCalls: number,
  totalDuration: string,
  fillClass: string,
): string {
  const barWidth = Math.max(0, Math.min(100, percent))
  const tooltip = `${esc(label)}: ${percent}% of total latency · avg ${esc(avgDuration)}/call · ${totalCalls} calls · total ${esc(totalDuration)}`
  return `<div class="pcontrib" title="${tooltip}">
  <div class="prow">
    <span class="prow-l">${esc(label)}</span>
    <span class="prow-v">${esc(avgDuration)} &middot; ${totalCalls} calls</span>
  </div>
  <div class="pbar" title="${esc(label)}: ${percent}% of total latency"><div class="pfill ${fillClass}" style="width:${barWidth}%" title="${esc(label)} · ${percent}% · total ${esc(totalDuration)}"></div></div>
  <div class="prow-sub">${percent}% &middot; total ${esc(totalDuration)}</div>
</div>`
}

function renderDetailTable(contributors: PerfStripProps['contributors']): string {
  const rows = contributors
    .map(
      (c) =>
        `<tr><td ${TD_L}>${esc(c.label)}</td>
        <td ${TH}>${esc(c.avgDuration)}</td>
        <td ${TH}>${c.totalCalls}</td>
        <td ${TH}>${esc(c.totalDuration)}</td>
        <td ${TH}>${c.percent}%</td></tr>`,
    )
    .join('')
  return `<table style="width:100%;border-collapse:collapse;margin-top:8px">
  <thead><tr style="border-bottom:1px solid var(--bd)">
    <th ${TH_L}>Tool</th>
    <th ${TH_L}>Avg Latency</th>
    <th ${TH_L}>Calls</th>
    <th ${TH_L}>Total Latency</th>
    <th ${TH_L}>% Total</th>
  </tr></thead>
  <tbody>${rows}</tbody>
</table>`
}

function renderToolSummaryCard(
  totalCalls: number,
  totalLLMLatency: string | undefined,
  totalLLMCalls: number,
  totalErrors: number,
): string {
  const latencyLine = totalLLMLatency
    ? `<span>${esc(totalLLMLatency)} total latency</span><span style="margin:0 4px">&middot;</span>`
    : ''
  return `<div class="pcard">
  <div class="pcard-t">Tool Calls</div>
  <div class="te-big">${totalCalls}</div>
  <div style="font-size:10px;color:var(--t3)">
    ${latencyLine}${totalLLMCalls} assistant calls
  </div>
  <div style="font-size:10px;color:var(--t3);margin-top:2px">
    ${totalErrors} errors
  </div>
</div>`
}

/** Render the performance/latency contributors strip. */
export function renderPerfStrip(props: PerfStripProps): string {
  const contributorsHtml = props.contributors
    .map((c, i) =>
      renderContributorRow(
        c.label,
        c.percent,
        c.avgDuration,
        c.totalCalls,
        c.totalDuration,
        FILL_COLORS[i % FILL_COLORS.length] ?? 'pf1',
      ),
    )
    .join('')

  const detailTable = renderDetailTable(props.contributors)
  const toolCard = renderToolSummaryCard(
    props.toolSummary.totalCalls,
    props.toolSummary.totalLLMLatency,
    props.toolSummary.totalLLMCalls,
    props.toolSummary.totalErrors,
  )

  const hasModelData = props.modelContributors && props.modelContributors.length > 0
  const modelCardHtml = hasModelData
    ? (props.modelContributors ?? [])
        .map((m, i) =>
          renderContributorRow(
            m.model,
            m.percent,
            '—',
            m.totalCalls,
            m.totalDuration,
            FILL_COLORS[i % FILL_COLORS.length] ?? 'pf1',
          ),
        )
        .join('')
    : ''
  const modelCard = hasModelData
    ? `<div class="pcard" role="list" aria-label="Latency by model breakdown">
  <div class="pcard-t">Latency by Model</div>
  ${modelCardHtml}
</div>`
    : ''

  return `<div class="perf">
  <div class="pcard" style="flex:2">
    <div class="pcard-t">Latency by Tool <span style="font-weight:400;font-size:9px;color:var(--t4)">(avg/call &middot; calls &middot; hover for detail)</span></div>
    ${contributorsHtml}
    <div class="perf-detail" id="perf-detail" style="display:none;margin-top:12px;border-top:1px solid var(--bd);padding-top:8px">
      <div style="font-size:9px;color:var(--t4);margin-bottom:4px">FULL BREAKDOWN</div>
      ${detailTable}
    </div>
    <button class="perf-toggle" id="perf-toggle-btn" style="background:none;border:none;cursor:pointer;color:var(--t4);font-size:9px;padding:4px 0;margin-top:4px" data-action="togglePerfDetail" data-arg="perf-detail">Show detail &#9660;</button>
  </div>
  ${modelCard}
  ${toolCard}
</div>`
}
